import React, { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import styled, { css } from "styled-components";
import { Modal } from "@material-ui/core";
import { useSnackbar } from "notistack";
import AWSService from "../../../../services/AWSService";
import useLoader from "../../../../hooks/useLoader";
import {
  getErrorMessage,
  getFileFormat,
  getFileName,
} from "../../../../helpers/functions";
import CaseService from "../../../../services/CaseService";
import DocViewers from "../../../internal/DrawerTable/DocViewer";
import FormField from "../../../common/FormField/FormField";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../../common/Buttons";
import {
  Overlay,
  ModalCard,
  ModalHeader,
  ModalBody,
  ModalTitle,
  FormSection,
  HeaderContent,
  IconBadge,
  CloseBtn,
  SectionLabel,
  ModeToggle,
  ModeBtn,
  HiddenInput,
} from "../../../common/MutipleHearings";
import theme from "../../../../assets/theme";
import COLORS from "../../../../assets/Colors";

const validFormat = /^image\//;

const PART_OPTIONS = [
  { label: "Part 1", value: "1" },
  { label: "Part 2", value: "2" },
];

const GENERATE_FIELDS = ["part", "invoiceNumber", "signFile"];

const inputs = [
  {
    name: "to",
    type: "email",
    label: "To",
    required: true,
  },
  {
    name: "cc",
    type: "text",
    label: "CC",
    required: true,
  },
];

const formatFileSize = (bytes = 0) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const InvoiceModal = ({
  open,
  onClose,
  booking,
  onSubmit,
  documentId,
  updatedFileWithCaseId,
  claimantEmails = [],
}) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [docview, setDocView] = useState({ state: false });
  const [ccSuggestions, setCcSuggestions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedFor, setGeneratedFor] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => preview && URL.revokeObjectURL(preview);
  }, [preview]);

  const validationSchema = Yup.object({
    part: Yup.string().required("Please select Part 1 or Part 2"),
    invoiceNumber: Yup.string()
      .trim()
      .required("Invoice number is required")
      .max(50, "Invoice number must be at most 50 characters")
      .test(
        "valid-invoice-number",
        "Invoice number must be in the format TN<Financial Year>DR<Number>, e.g. TN2026-27DR001",
        (value) => {
          if (!value) return false;
          const match = value.match(/^TN(\d{4})-?(\d{2})DR(\d{3,})$/);
          if (!match) return false;
          const startYear = parseInt(match[1], 10);
          const endYear = parseInt(match[2], 10);
          return (startYear + 1) % 100 === endYear;
        },
      ),
    signFile: Yup.mixed().nullable().required("Sign image is required"),
    to: Yup.string()
      .required("To email is required")
      .test(
        "valid-to-emails",
        "Please enter valid email address(es)",
        (value) => {
          if (!value) return false;
          const emails = value.split(",").map((e) => e.trim());
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emails.every(
            (email) => emailRegex.test(email) && email.length > 5,
          );
        },
      ),
    cc: Yup.string().test(
      "valid-cc-emails",
      "Please enter valid email address(es) in CC",
      (value) => {
        if (!value || value.trim() === "") return true;
        const emails = value
          .split(",")
          .map((e) => e.trim())
          .filter((e) => e.length > 0);
        if (emails.length === 0) return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails.every((email) => emailRegex.test(email));
      },
    ),
  });

  const onFormSubmit = async (values) => {
    try {
      setLoader({ state: true, message: `Email send ...` });
      const response = await CaseService.sendInvoiceMail({
        fileUrl: values.fileUrl,
        to: values.to,
        cc: values.cc,
        paymentId: values.paymentId,
        part: values.part,
      });
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
      onClose();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const openPreview = (fileUrl) =>
    setDocView({
      state: true,
      url: fileUrl,
      name: getFileName(fileUrl),
      format: getFileFormat(fileUrl),
    });

  function selectFile(file, setFieldValue, setFieldTouched) {
    if (!file) return;
    setFieldTouched("signFile", true, false);
    if (!validFormat.test(file?.type)) {
      enqueueSnackbar("Invalid file type. Please choose an image.", {
        variant: "error",
      });
      return;
    }
    setFieldValue("signImageUrl", "", false);
    setFieldValue("signFile", file, true);
    setPreview(URL.createObjectURL(file));
  }

  function removeFile(setFieldValue) {
    setFieldValue("signImageUrl", "", false);
    setFieldValue("signFile", null, true);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function generateInvoice(values, setFieldValue) {
    const file = values.signFile;
    try {
      setGenerating(true);

      let signImageUrl = values.signImageUrl;
      if (!signImageUrl) {
        setLoader({ state: true, message: `Uploading file ${file.name} ...` });
        const s3Response = await AWSService.getS3URL({ key: file.name });
        if (!s3Response?.url) throw new Error("Could not upload this file");
        signImageUrl = await AWSService.uploadToS3(s3Response.url, file);
        setFieldValue("signImageUrl", signImageUrl, false);
      }

      setLoader({ state: true, message: "Generating invoice ..." });
      const invoice_response = await CaseService.generateInvoiceBulk({
        documentId,
        updatedFileWithCaseId,
        signImageUrl,
        invoicePart: values.part,
        invoiceNumber: values.invoiceNumber.trim(),
      });
      setFieldValue("fileUrl", invoice_response?.fileUrl, false);
      setFieldValue("paymentId", invoice_response?.paymentId, false);

      const responseCcEmails = Array.isArray(invoice_response?.emailIds)
        ? invoice_response.emailIds
        : [];

      if (responseCcEmails.length) {
        const mergedCcEmails = Array.from(new Set(responseCcEmails))
          .map((e) => e.trim())
          .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
        setCcSuggestions(mergedCcEmails);
        setFieldValue("cc", mergedCcEmails.join(", "), false);
      }

      setGeneratedFor({
        part: values.part,
        invoiceNumber: values.invoiceNumber.trim(),
        signFile: file,
      });
      openPreview(invoice_response?.fileUrl);
      enqueueSnackbar("Invoice generated successfully", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setGenerating(false);
      setLoader({ state: false });
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Overlay>
        <ModalCard>
          <ModalHeader>
            <HeaderContent>
              <IconBadge>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 .7V2l-1 .7-3-2-3 2-3-2-3 2-3-2z" />
                  <line x1="8" y1="8" x2="16" y2="8" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                  <line x1="8" y1="16" x2="12" y2="16" />
                </svg>
              </IconBadge>
              <div>
                <ModalTitle>Generate Invoice</ModalTitle>
                <SubTitle>
                  Fill in the details, generate the PDF, then preview and share
                  it.
                </SubTitle>
              </div>
            </HeaderContent>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseBtn>
          </ModalHeader>

          <Formik
            initialValues={{
              part: "1",
              invoiceNumber: "",
              signFile: null,
              signImageUrl: "",
              to: claimantEmails.join(", "),
              cc: "",
              paymentId: "",
              fileUrl: "",
            }}
            validationSchema={validationSchema}
            onSubmit={onFormSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              setFieldError,
              setFieldTouched,
              validateForm,
              handleBlur,
              handleChange,
              handleSubmit,
            }) => {
              const isGenerated =
                !!values.fileUrl &&
                !!generatedFor &&
                generatedFor.part === values.part &&
                generatedFor.invoiceNumber === values.invoiceNumber.trim() &&
                generatedFor.signFile === values.signFile;

              const onGenerateClick = async () => {
                GENERATE_FIELDS.forEach((f) => setFieldTouched(f, true, false));
                const formErrors = await validateForm();
                if (GENERATE_FIELDS.some((f) => formErrors[f])) return;
                generateInvoice(values, setFieldValue);
              };

              const fileError =
                !values.signFile && touched.signFile && errors.signFile;

              return (
                <>
                  <FormLayout
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!generating) onGenerateClick();
                    }}
                  >
                    <ModalBody>
                      <StepList>
                        <Step>
                          <StepHeader>
                            <StepNumber $done={!!values.part}>1</StepNumber>
                            <SectionLabel style={{ margin: 0 }}>
                              Invoice Part
                              <RequiredStar>*</RequiredStar>
                            </SectionLabel>
                          </StepHeader>
                          <ModeToggle>
                            {PART_OPTIONS.map((opt) => (
                              <ModeBtn
                                key={opt.value}
                                type="button"
                                active={values.part === opt.value}
                                onClick={() => setFieldValue("part", opt.value)}
                              >
                                {opt.label}
                              </ModeBtn>
                            ))}
                          </ModeToggle>
                          {touched.part && errors.part && (
                            <ErrorText>{errors.part}</ErrorText>
                          )}
                        </Step>

                        <Step>
                          <StepHeader>
                            <StepNumber $done={!!values.invoiceNumber.trim()}>
                              2
                            </StepNumber>
                            <SectionLabel style={{ margin: 0 }}>
                              Invoice Details
                            </SectionLabel>
                          </StepHeader>
                          <FormField
                            white
                            maxLength={50}
                            input={{
                              type: "text",
                              name: "invoiceNumber",
                              label: "Invoice Number",
                              placeholder: "e.g. TN2026-27DR001",
                              required: true,
                            }}
                            {...{
                              values,
                              errors,
                              touched,
                              handleBlur,
                              handleChange,
                              setFieldValue,
                            }}
                          />
                        </Step>

                        <Step>
                          <StepHeader>
                            <StepNumber $done={!!values.signFile}>3</StepNumber>
                            <SectionLabel style={{ margin: 0 }}>
                              Upload Sign
                              <RequiredStar>*</RequiredStar>
                            </SectionLabel>
                          </StepHeader>

                          <HiddenInput
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              selectFile(
                                e.target.files?.[0],
                                setFieldValue,
                                setFieldTouched,
                              );
                              e.target.value = "";
                            }}
                          />

                          {values.signFile ? (
                            <FileCard>
                              <Thumb>
                                {preview && (
                                  <img src={preview} alt="Sign preview" />
                                )}
                              </Thumb>
                              <FileMeta>
                                <FileName title={values.signFile.name}>
                                  {values.signFile.name}
                                </FileName>
                                <FileSub>
                                  {formatFileSize(values.signFile.size)} · Ready
                                  to upload
                                </FileSub>
                              </FileMeta>
                              <FileActions>
                                <LinkBtn
                                  type="button"
                                  onClick={() => openFilePicker()}
                                  disabled={generating}
                                >
                                  Change
                                </LinkBtn>
                                <LinkBtn
                                  type="button"
                                  $danger
                                  onClick={() => removeFile(setFieldValue)}
                                  disabled={generating}
                                >
                                  Remove
                                </LinkBtn>
                              </FileActions>
                            </FileCard>
                          ) : (
                            <DropZone
                              role="button"
                              tabIndex={0}
                              $dragging={dragging}
                              $error={!!fileError}
                              onClick={() => openFilePicker()}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openFilePicker();
                                }
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                setDragging(true);
                              }}
                              onDragLeave={() => setDragging(false)}
                              onDrop={(e) => {
                                e.preventDefault();
                                setDragging(false);
                                selectFile(
                                  e.dataTransfer.files?.[0],
                                  setFieldValue,
                                  setFieldTouched,
                                );
                              }}
                            >
                              <DropIcon>
                                <svg
                                  width="22"
                                  height="22"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="17 8 12 3 7 8" />
                                  <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                              </DropIcon>
                              <DropPrimary>
                                <span>Click to upload</span> or drag and drop
                              </DropPrimary>
                              <DropSub>PNG, JPG or any image file</DropSub>
                            </DropZone>
                          )}
                          {fileError && <ErrorText>{fileError}</ErrorText>}
                        </Step>
                      </StepList>

                      {isGenerated && (
                        <SuccessCard>
                          <SuccessIcon>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </SuccessIcon>
                          <div>
                            <SuccessTitle>Invoice generated</SuccessTitle>
                            <SuccessSub>
                              {values.invoiceNumber.trim()} · Part {values.part}
                              . Preview it and send it by email.
                            </SuccessSub>
                          </div>
                        </SuccessCard>
                      )}
                      {!!values.fileUrl && !isGenerated && (
                        <InfoCard>
                          Details changed — submit again to regenerate the
                          invoice.
                        </InfoCard>
                      )}
                    </ModalBody>

                    <StickyFooter>
                      <PrimaryOutlinedCloseCTAButton
                        type="button"
                        onClick={onClose}
                        disabled={generating}
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </PrimaryOutlinedCloseCTAButton>
                      {isGenerated ? (
                        <PrimaryCTAButton
                          type="button"
                          onClick={() => openPreview(values.fileUrl)}
                          style={{ flex: 1 }}
                        >
                          Preview &amp; Share
                        </PrimaryCTAButton>
                      ) : (
                        <PrimaryCTAButton
                          type="submit"
                          disabled={generating}
                          style={{ flex: 1 }}
                        >
                          {generating ? "Generating..." : "Submit"}
                        </PrimaryCTAButton>
                      )}
                    </StickyFooter>
                  </FormLayout>

                  {docview?.state && (
                    <DocViewers
                      docview={docview}
                      setDocView={setDocView}
                      to={values?.to || ""}
                      cc={values?.cc || ""}
                      setFieldValue={setFieldValue}
                      inputs={inputs}
                      share={true}
                      fileDownload={false}
                      emailSuggestions={ccSuggestions}
                      handleSubmit={() =>
                        onFormSubmit(values, { setSubmitting: () => {} })
                      }
                      {...{
                        values,
                        handleChange,
                        errors,
                        handleSubmit,
                        touched,
                        setFieldValue,
                        setFieldError,
                        handleBlur,
                      }}
                    />
                  )}
                </>
              );
            }}
          </Formik>
        </ModalCard>
      </Overlay>
    </Modal>
  );
};

export default InvoiceModal;

// Form spans body + footer so header/footer stay fixed and only ModalBody scrolls
const FormLayout = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
`;

const StickyFooter = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  padding: 16px 24px;
  border-top: 1px solid #f0f2f5;
  background: #fff;
  border-radius: 0 0 20px 20px;
  @media (max-width: 480px) {
    padding: 12px 16px;
    border-radius: 0;
  }
  @media (max-width: 400px) {
    flex-direction: column-reverse;
  }
`;

const SubTitle = styled.div`
  margin-top: 2px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #6b7280;
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Step = styled(FormSection)`
  margin-bottom: 0;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const StepNumber = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  transition: all 0.2s ease;
  ${(p) =>
    p.$done
      ? css`
          background: ${COLORS.BTN_GREEN};
          color: #fff;
        `
      : css`
          background: #f4f6f8;
          color: #6b7280;
        `}
`;

const RequiredStar = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const ErrorText = styled.div`
  margin-top: 6px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  color: #ef4444;
`;

const DropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 24px 16px;
  border-radius: 14px;
  border: 1.5px dashed
    ${(p) =>
      p.$error ? "#ef4444" : p.$dragging ? COLORS.BTN_GREEN : "#d1d5db"};
  background: ${(p) => (p.$dragging ? `${COLORS.BTN_GREEN}0d` : "#fafafa")};
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
  &:hover,
  &:focus-visible {
    border-color: ${COLORS.BTN_GREEN};
    background: ${COLORS.BTN_GREEN}08;
    outline: none;
  }
`;

const DropIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.BTN_GREEN}14;
  color: ${COLORS.BTN_GREEN};
  margin-bottom: 2px;
`;

const DropPrimary = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13.5px;
  color: #374151;
  span {
    color: ${COLORS.BTN_GREEN};
  }
`;

const DropSub = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #9ca3af;
`;

const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1.5px solid ${COLORS.BTN_GREEN}55;
  background: ${COLORS.BTN_GREEN}08;
`;

const Thumb = styled.div`
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e5e7eb;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const FileMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSub = styled.div`
  margin-top: 2px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #6b7280;
`;

const FileActions = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const LinkBtn = styled.button`
  border: none;
  background: transparent;
  padding: 6px 8px;
  border-radius: 8px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  cursor: pointer;
  color: ${(p) => (p.$danger ? "#ef4444" : COLORS.BTN_GREEN)};
  transition: background 0.18s ease;
  &:hover:not(:disabled) {
    background: ${(p) => (p.$danger ? "#fee2e2" : `${COLORS.BTN_GREEN}14`)};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 20px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
`;

const SuccessIcon = styled.div`
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #10b981;
  color: #fff;
`;

const SuccessTitle = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: #065f46;
`;

const SuccessSub = styled.div`
  margin-top: 2px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #047857;
`;

const InfoCard = styled.div`
  margin-top: 20px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  color: #92400e;
`;
